import { ChangeDetectionStrategy, Component, OnInit, forwardRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';

import { ProviderShell } from '../shared/provider-shell';
import { ProviderShellLayoutComponent } from '../shared/provider-shell-layout.component';
import { ProviderDiagnosticsAdminService } from '../services/provider-diagnostics-admin.service';
import { FeedDiagnostics } from '../../../shared/models/feed-diagnostics.model';
import { GTFS_PROVIDER_TYPE } from '../util/gtfs-config';

/**
 * GTFS provider shell (/admin/providers/gtfs/:providerId). On top of the
 * shared shell it owns one cross-tenant diagnostics fetch
 * (`ProviderDiagnosticsAdminService.getAll('gtfs')`), joined by feedId in the
 * GTFS Feeds and Details tabs rather than N+1 per-feed requests.
 *
 * Uses the shared ProviderShellLayoutComponent chrome (header + Feeds | Details
 * tabs), same as SIRI, so the provider screens look alike above the content.
 */
@Component({
  selector: 'app-gtfs-provider-shell',
  standalone: true,
  imports: [RouterOutlet, ProviderShellLayoutComponent],
  template: `<app-provider-shell-layout><router-outlet /></app-provider-shell-layout>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ProviderShell, useExisting: forwardRef(() => GtfsProviderShellComponent) }],
  host: { class: 'block h-full min-h-0' },
})
export class GtfsProviderShellComponent extends ProviderShell implements OnInit {
  private readonly diagnosticsAdminService = inject(ProviderDiagnosticsAdminService);

  readonly routeType = GTFS_PROVIDER_TYPE;

  /** Latest diagnostics snapshot per feed, keyed by feedId. Empty map (not an
   *  error state) until loaded, or if the fetch fails. Only core-managed feeds
   *  are ever reported, so a feed may simply have no entry. */
  private readonly _diagnostics = signal<Map<string, FeedDiagnostics>>(new Map());
  readonly diagnostics = this._diagnostics.asReadonly();
  readonly isDiagnosticsLoading = signal(false);

  override ngOnInit(): void {
    super.ngOnInit();
    this.fetchDiagnostics();
  }

  override refetchFeeds(): void {
    super.refetchFeeds();
    this.fetchDiagnostics();
  }

  /** Soft-fails silently (no toast) — feeds just show "No snapshot". */
  private fetchDiagnostics(): void {
    this.isDiagnosticsLoading.set(true);
    this.diagnosticsAdminService
      .getAll(GTFS_PROVIDER_TYPE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this._diagnostics.set(new Map(res.data.map((d) => [d.feedId, d])));
          this.isDiagnosticsLoading.set(false);
        },
        error: () => this.isDiagnosticsLoading.set(false),
      });
  }
}
